/**
 * @file WaiterOrderCard.tsx
 * @description High-contrast touch-first order card for TableGreet Waiter expediting.
 * 
 * REQUIRED CONTRACTS:
 * 1. Table num, ID, Department (if existed), Date when, Price.
 * 2. Payment method with clear indicators for Card, Online Stripe, or Cash.
 * 3. Service Pipeline Status: Status (New, Preparing, Ready, Done) + Allergens & dietary alerts.
 * 4. Order Details: Full breakdown of what the clients ordered.
 * 5. Action button: "Confirm to Kitchen" button to move new orders to the kitchen queue.
 */

import React from 'react';
import type { WaiterActiveOrder } from '../../../types/waiterOrder.types';
import {
  Clock,
  ChefHat,
  Flame,
  CheckCircle2,
  HandPlatter,
  CreditCard,
  Banknote,
  Bell,
  Utensils,
  ChevronRight,
  AlertTriangle,
  Building2,
  Calendar,
  Send,
  Droplets,
  Receipt,
  Sparkles,
  Ban,
  Wine,
} from 'lucide-react';

interface WaiterOrderCardProps {
  order: WaiterActiveOrder;
  onConfirmToKitchen: (orderId: string) => void;
  onServe: (orderId: string) => void;
  onCollectPayment: (orderId: string, method: 'CASH' | 'CARD') => void;
  onResolveCall: (requestId: string) => void;
  onOpenWorkspace: (order: WaiterActiveOrder) => void;
  onVoidOrder?: (order: WaiterActiveOrder) => void;
}

export const WaiterOrderCard = React.memo<WaiterOrderCardProps>(({
  order,
  onConfirmToKitchen,
  onServe,
  onCollectPayment,
  onResolveCall,
  onOpenWorkspace,
  onVoidOrder,
}) => {
  const isNew = order.pipeline_status === 'new';
  const isPreparing = order.pipeline_status === 'preparing';
  const isReady = order.pipeline_status === 'ready';
  const isDone = order.pipeline_status === 'done';

  const isPaidOnline = order.payment_method === 'ONLINE' && order.payment_status === 'paid';
  const isCard = order.payment_method === 'CARD';

  const hasBillRequest = order.guest_action === 'bill_requested';
  const hasWaterRequest = order.guest_action === 'water_requested';
  const hasCleanRequest = order.guest_action === 'clean_requested';
  const hasCallRequest =
    order.guest_action === 'call_requested' || hasWaterRequest || hasCleanRequest;

  // Calculates total customized modifiers across all items in this order
  const totalModifiersCount = React.useMemo(() => {
    let count = 0;
    for (const item of order.items || []) {
      if (Array.isArray(item.modifiers)) {
        count += item.modifiers.length;
      } else if (typeof item.modifiers === 'string' && (item.modifiers as string).trim()) {
        try {
          const parsed = JSON.parse(item.modifiers);
          if (Array.isArray(parsed)) count += parsed.length;
          else count += 1;
        } catch {
          count += 1;
        }
      }
    }
    return count;
  }, [order.items]);

  // Identifies whether all items in this order are beverage/bar items handled by waitstaff
  const isOnlyDrinks = React.useMemo(() => {
    return order.items.length > 0 && order.items.every(
      i => (i.kitchen_station || '').toLowerCase() === 'drinks'
    );
  }, [order.items]);

  return (
    <div
      className={`rounded-3xl border transition-all duration-200 flex flex-col justify-between overflow-hidden bg-[#121212] ${
        isReady
          ? 'border-purple-500/80 shadow-xl shadow-purple-950/40 ring-1 ring-purple-500/40'
          : isNew
          ? 'border-[#ff4f00]/70 shadow-xl shadow-[#ff4f00]/20 ring-1 ring-[#ff4f00]/30'
          : hasBillRequest
          ? 'border-rose-500/80 shadow-xl shadow-rose-950/40 ring-1 ring-rose-500/40'
          : isDone
          ? 'border-emerald-800/40 opacity-90'
          : 'border-white/10 hover:border-white/20'
      }`}
    >
      {/* 1. Header: Table Num, ID, Department (if existed), Date when, Price */}
      <div className="p-4 bg-white/[0.02] border-b border-white/5 space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-black text-white tracking-tight leading-none uppercase">
                {order.table_number}
              </h3>
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-md bg-white/10 text-zinc-300">
                {order.order_number}
              </span>
            </div>

            {/* Department (if existed and not raw UUID) */}
            {order.department && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(order.department) && (
              <div className="flex items-center gap-1.5 mt-1 text-xs text-amber-400 font-semibold">
                <Building2 className="w-3.5 h-3.5 shrink-0" />
                <span>Department: {order.department}</span>
              </div>
            )}
          </div>

          {/* Price & Currency */}
          <div className="text-right shrink-0">
            <div className="text-lg font-black text-[#ff4f00] tracking-tight">
              {order.total_amount.toFixed(2)} {order.currency}
            </div>
            {/* Payment Method Badge */}
            <div className="mt-1">
              {isPaidOnline ? (
                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800/60">
                  <CreditCard className="w-2.5 h-2.5" />
                  <span>Paid Online</span>
                </span>
              ) : isCard ? (
                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-950 text-blue-300 border border-blue-800/60">
                  <CreditCard className="w-2.5 h-2.5" />
                  <span>Card {order.payment_status === 'paid' ? '(Paid)' : ''}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-white/5">
                  <Banknote className="w-2.5 h-2.5" />
                  <span>Cash {order.payment_status === 'paid' ? '(Paid)' : '(Unpaid)'}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Date When & Elapsed Time */}
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
          <span className="font-semibold text-zinc-300">{order.formatted_date}</span>
          <span>•</span>
          {isReady ? (
            <span className="inline-flex items-center gap-1 font-mono font-bold text-xs text-purple-300 bg-purple-950/70 border border-purple-700/60 px-2 py-0.5 rounded animate-pulse">
              <Clock className="w-3 h-3 text-purple-400" />
              <span>AT PASS ({order.ready_at ? `${Math.max(0, Math.floor((Date.now() - new Date(order.ready_at).getTime()) / 60000))}m` : `${order.elapsed_minutes}m`})</span>
            </span>
          ) : (
            <span className="flex items-center gap-1 text-zinc-400">
              <Clock className="w-3 h-3 text-zinc-500" />
              <span>{order.elapsed_minutes}m ago</span>
            </span>
          )}
          {order.guest_count > 0 && (
            <>
              <span>•</span>
              <span>{order.guest_count} guests</span>
            </>
          )}
        </div>
      </div>

      {/* 2. Public Menu Guest Alert Banner (if assistance or bill requested) */}
      {(hasCallRequest || hasBillRequest) && order.guest_action_id && (
        <div
          className={`px-4 py-2.5 flex items-center justify-between text-xs font-bold border-b ${
            hasBillRequest
              ? 'bg-rose-950/70 border-rose-900/60 text-rose-200'
              : 'bg-amber-950/70 border-amber-900/60 text-amber-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {hasBillRequest ? (
              <Receipt className="w-4 h-4 animate-bounce text-rose-300 shrink-0" />
            ) : hasWaterRequest ? (
              <Droplets className="w-4 h-4 animate-bounce text-cyan-300 shrink-0" />
            ) : hasCleanRequest ? (
              <Sparkles className="w-4 h-4 animate-bounce text-amber-300 shrink-0" />
            ) : (
              <Bell className="w-4 h-4 animate-bounce text-amber-300 shrink-0" />
            )}
            <span>
              {hasBillRequest
                ? 'Guest requested the bill from Public Menu'
                : hasWaterRequest
                ? 'Guest requested water from Public Menu'
                : hasCleanRequest
                ? 'Guest requested table cleanup'
                : 'Guest called for staff assistance'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onResolveCall(order.guest_action_id!)}
            className="px-2.5 py-1 rounded-lg bg-black/40 hover:bg-black/60 text-white text-[10px] font-black uppercase tracking-wider cursor-pointer shrink-0 transition-colors"
          >
            Mark Attended
          </button>
        </div>
      )}

      {/* 3. Service Pipeline Status (Pipeline Stage + Allergens & Dietary) */}
      <div className="p-3 bg-black/40 border-b border-white/5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
            Service Pipeline Status
          </span>
          {/* Status Badge: New, Sent to Kitchen, Preparing, Ready, Done */}
          <span
            className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
              isNew && order.is_confirmed_to_kitchen
                ? 'bg-blue-950 text-blue-300 border border-blue-800/60'
                : isNew
                ? 'bg-[#ff4f00]/20 text-[#ff4f00] border border-[#ff4f00]/40 animate-pulse'
                : isPreparing
                ? 'bg-amber-950 text-amber-300 border border-amber-800/60'
                : isReady
                ? 'bg-purple-950 text-purple-200 border border-purple-500/80 shadow-md shadow-purple-950/50 animate-pulse font-black'
                : 'bg-emerald-950 text-emerald-300 border border-emerald-800/60'
            }`}
          >
            {isNew && order.is_confirmed_to_kitchen ? (
              <>
                <Clock className="w-3 h-3 text-blue-400" />
                <span>SENT TO KITCHEN</span>
              </>
            ) : isNew ? (
              <>
                <ChefHat className="w-3 h-3" />
                <span>NEW</span>
              </>
            ) : isPreparing ? (
              <>
                <Flame className="w-3 h-3" />
                <span>PREPARING</span>
              </>
            ) : isReady ? (
              <>
                <CheckCircle2 className="w-3 h-3 text-purple-300" />
                <span>HOT FOOD READY AT PASS</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3 h-3" />
                <span>DONE</span>
              </>
            )}
          </span>
        </div>

        {/* Allergens & Dietary Information */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {order.allergens && order.allergens.length > 0 ? (
            <div className="px-2 py-0.5 rounded-md bg-rose-950/60 border border-rose-800/60 text-rose-300 text-[10px] font-bold flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" />
              <span>Allergens: {order.allergens.join(', ')}</span>
            </div>
          ) : (
            <div className="px-2 py-0.5 rounded-md bg-zinc-800/80 text-zinc-400 text-[10px] font-medium">
              No allergens listed
            </div>
          )}

          {order.customer_notes && (
            <div className="px-2 py-0.5 rounded-md bg-amber-950/40 border border-amber-800/40 text-amber-300 text-[10px] font-medium italic">
              Dietary Note: {order.customer_notes}
            </div>
          )}
        </div>
      </div>

      {/* 4. Order Details: What the clients ordered */}
      <div className="p-4 flex-1 space-y-2 overflow-y-auto max-h-52 scrollbar-none">
        <p className="text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1">
          Order Details
        </p>
        {order.items.length === 0 ? (
          <p className="text-xs text-zinc-500 italic">No items listed</p>
        ) : (
          order.items.map((item, idx) => (
            <div key={idx} className="flex items-start justify-between text-xs gap-2">
              <div className="flex items-start gap-2">
                <span className="font-mono font-black text-[#ff4f00] shrink-0">
                  {item.quantity}x
                </span>
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-zinc-200 font-semibold">{item.name}</span>
                    {((item as any).kitchen_station || '').toLowerCase() === 'drinks' && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.2 rounded">
                        <Wine className="w-2.5 h-2.5" />
                        Bar / Drinks
                      </span>
                    )}
                  </div>
                  {item.modifiers && item.modifiers.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {item.modifiers.map((mod: any, mIdx: number) => {
                        const modName = typeof mod === 'string' ? mod : (mod.name || 'Modifier');
                        return (
                          <span
                            key={mIdx}
                            className="inline-flex items-center text-[10px] font-semibold text-rose-300 bg-rose-950/60 border border-rose-900/60 px-1.5 py-0.2 rounded"
                          >
                            + {modName}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {item.notes && (
                    <p className="text-[10px] text-amber-400/90 italic mt-0.5">
                      Note: {item.notes}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-zinc-400 font-mono text-[11px] shrink-0">
                {(item.price * item.quantity).toFixed(2)}
              </span>
            </div>
          ))
        )}
      </div>

      {/* 5. Touch Action Footer with Confirm Button to Kitchen */}
      <div className="p-3 bg-black/60 border-t border-white/5 space-y-2">
        {/* Confirm Button to Move Order to Kitchen, or Direct Serve for Drinks-Only Orders */}
        {isNew && !order.is_confirmed_to_kitchen && isOnlyDrinks && (
          <button
            type="button"
            onClick={() => onServe(order.id)}
            className="w-full py-3 px-4 rounded-2xl bg-amber-600 hover:bg-amber-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-amber-900/40 cursor-pointer transition-all active:scale-[0.98]"
          >
            <Wine className="w-4 h-4" />
            <span>Serve Drinks (Direct Bar Order)</span>
          </button>
        )}

        {isNew && !order.is_confirmed_to_kitchen && !isOnlyDrinks && (
          <button
            type="button"
            onClick={() => onConfirmToKitchen(order.id)}
            className="w-full py-3 px-4 rounded-2xl bg-[#ff4f00] hover:bg-[#e64700] text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-[#ff4f00]/30 cursor-pointer transition-all active:scale-[0.98]"
          >
            <Send className="w-4 h-4" />
            <span>Confirm Food to Kitchen</span>
          </button>
        )}

        {isNew && order.is_confirmed_to_kitchen && (
          <div className="w-full py-2.5 px-3 rounded-2xl bg-blue-950/40 border border-blue-800/50 text-blue-300 font-bold text-xs flex items-center justify-center gap-2">
            <Clock className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            <span>SENT TO KITCHEN • Waiting for Chef</span>
          </div>
        )}

        {isReady && (
          <button
            type="button"
            onClick={() => onServe(order.id)}
            className="w-full py-3 px-4 rounded-2xl bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-purple-900/50 cursor-pointer transition-all active:scale-[0.98]"
          >
            <HandPlatter className="w-4 h-4" />
            <span>Mark Served to Table</span>
          </button>
        )}

        {(hasBillRequest || (!isPaidOnline && order.payment_status === 'unpaid' && !isNew)) && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onCollectPayment(order.id, 'CASH')}
              className="flex-1 py-2.5 px-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-950/40 cursor-pointer transition-all active:scale-[0.98]"
            >
              <Banknote className="w-4 h-4" />
              <span>Collect Cash</span>
            </button>
            <button
              type="button"
              onClick={() => onCollectPayment(order.id, 'CARD')}
              className="flex-1 py-2.5 px-3 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg shadow-blue-950/40 cursor-pointer transition-all active:scale-[0.98]"
            >
              <CreditCard className="w-4 h-4" />
              <span>Paid Card</span>
            </button>
          </div>
        )}

        {/* Manage / add items or Void — hidden once the order is fully done */}
        {!isDone && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onOpenWorkspace(order)}
              className="flex-1 py-2.5 px-3 min-h-[42px] rounded-xl bg-white/5 hover:bg-white/10 active:scale-[0.98] text-zinc-300 hover:text-white font-bold text-xs flex items-center justify-between transition-all cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <Utensils className="w-3.5 h-3.5 text-zinc-500" />
                <span>Manage Order / Add Items</span>
                {totalModifiersCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white font-mono font-black text-[11px] shadow-sm shadow-rose-950">
                    {totalModifiersCount}
                  </span>
                )}
              </span>
              <ChevronRight className="w-4 h-4 text-zinc-500" />
            </button>
            {onVoidOrder && (
              <button
                type="button"
                onClick={() => onVoidOrder(order)}
                title="Void / Cancel Order"
                aria-label="Void or cancel order"
                className="min-w-[42px] min-h-[42px] flex items-center justify-center rounded-xl bg-white/5 hover:bg-rose-950/40 active:scale-[0.98] text-zinc-500 hover:text-rose-400 border border-transparent hover:border-rose-900 transition-all cursor-pointer shrink-0"
              >
                <Ban className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
